import type { ReactNode } from 'react';

import { Badge, Card } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

export type StepCardProps = {
  /** `center` for the done view; `start` (default) for the form steps and the waiting view. */
  align?: 'center' | 'start';
  /** Optional: the channel-verified confirmation is a card with a header and nothing below it. */
  children?: ReactNode;
  description?: ReactNode;
  /** The icon above the title. */
  icon?: ReactNode;
  /** For example «Paso 1 · correo y contraseña». Not used in the waiting and done views. */
  stepLabel?: string;
  title: string;
};

/**
 * The white card of the right panel, one container for every content of the wizard. Almost every
 * prop is optional because the variants differ only in what they leave out.
 *
 * @param {StepCardProps} props - Component properties
 */
export const StepCard = ({ align = 'start', children, description, icon, stepLabel, title }: StepCardProps) => (
  <Card
    className="flex w-full flex-col gap-6 border-0 bg-transparent p-0 font-sans shadow-none md:border md:bg-card md:p-7 md:shadow-xs"
    data-testid="identity-step-card"
  >
    <div className={cn('flex flex-col gap-3', { 'items-center text-center': align === 'center' })}>
      {icon}
      {stepLabel && (
        <Badge className="w-fit text-xs font-semibold" variant="secondary">
          {stepLabel}
        </Badge>
      )}
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
    {children}
  </Card>
);
